import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { LucideIcon } from "lucide-react";

interface FeatureHeroProps {
  badge?: string;
  title: string;
  titleHighlight?: string;
  titleEnd?: string;
  subtitle: string;
  primaryCTA?: {
    text: string;
    href: string;
  };
  secondaryCTA?: {
    text: string;
    href: string;
  };
  icon?: LucideIcon;
  gradient?: string;
}

const FeatureHero = ({
  badge,
  title,
  titleHighlight,
  titleEnd,
  subtitle,
  primaryCTA = { text: "Essayer gratuitement", href: "/auth?mode=signup" },
  secondaryCTA,
  icon: Icon,
  gradient = "from-[#F59E0B] to-[#D97706]",
}: FeatureHeroProps) => {
  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden bg-[#0A0A0F]">
      {/* Gradient mesh background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F172A] to-[#0A0A0F]" />
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/8 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-[#1A365D]/30 rounded-full blur-[120px]" />
      </div>

      {/* Subtle grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:64px_64px]" />

      <div className="container relative z-10 mx-auto px-4 py-32 pt-40">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          {badge && (
            <div className="inline-flex items-center gap-2.5 rounded-full border border-white/[0.08] bg-white/[0.03] px-4 py-2 backdrop-blur-sm animate-fade-up mb-8">
              {Icon && (
                <div className={`h-6 w-6 rounded-md bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                  <Icon className="h-3.5 w-3.5 text-white" />
                </div>
              )}
              <span className="text-sm font-medium text-white/70 uppercase tracking-wider">
                {badge}
              </span>
            </div>
          )}

          {/* Main Heading */}
          <h1 className="animate-fade-up [animation-delay:100ms] mb-6">
            <span className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.1]">
              {title}
            </span>
            {titleHighlight && (
              <span className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] mt-2">
                <span className="bg-gradient-to-r from-[#F59E0B] via-[#FBBF24] to-[#F59E0B] bg-clip-text text-transparent bg-[length:200%_auto] animate-shimmer">
                  {titleHighlight}
                </span>
              </span>
            )}
            {titleEnd && (
              <span className="block text-[2.5rem] sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white/90 leading-[1.1] mt-2">
                {titleEnd}
              </span>
            )}
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-white/50 leading-relaxed max-w-2xl mx-auto animate-fade-up [animation-delay:200ms] mb-10">
            {subtitle}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up [animation-delay:300ms]">
            <Button
              size="lg"
              className="h-12 px-6 bg-[#F59E0B] hover:bg-[#FBBF24] text-[#0A0A0F] font-semibold text-base gap-2.5 rounded-xl shadow-[0_0_0_1px_rgba(245,158,11,0.3),0_4px_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_0_1px_rgba(245,158,11,0.5),0_8px_30px_rgba(245,158,11,0.4)] hover:scale-[1.02] transition-all duration-300"
              asChild
            >
              <Link to={primaryCTA.href}>
                {primaryCTA.text}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            {secondaryCTA && (
              <Button
                size="lg"
                variant="ghost"
                className="h-12 px-6 border border-white/[0.08] bg-white/[0.02] text-white/80 hover:text-white hover:bg-white/[0.05] hover:border-white/[0.15] font-medium text-base gap-2.5 rounded-xl transition-all duration-300"
                asChild
              >
                <Link to={secondaryCTA.href}>
                  <Play className="h-4 w-4 fill-current" />
                  {secondaryCTA.text}
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureHero;
